import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../context/AuthContext';
import { useLocationInfo } from '../useLocationInfo';
import getEnvVar from '@/utils/getEnvVar';
import { AdobeAnalyticsConst } from '../adobeAnalyticsConstants/adobeAnalyticsConstants';
import { AdobeAnalyticsUserRole } from '../AdobeAnalyticsUserRole';
import { useEventTracker } from '../eventTracker/useEventTracker';
import { sendSignOutBeacon } from '../sendSignOutBeacon/sendSignOutBeacon';

const useSignOut = () => {
  const locationInfo = useLocationInfo();
  const { i18n } = useTranslation();
  const { userInfo, isLoggedIn } = useAuthContext();

  const buildPayload = useCallback(
    () => ({
      timestamp: new Date().toISOString(),
      page: {
        siteName: AdobeAnalyticsConst.SITE_DETAILS.name,
        sysEnv: getEnvVar('MODE'),
        language: i18n.language,
        url: locationInfo.url,
        domain: locationInfo.hostname,
        path: locationInfo.pathname,
        pageTitle: document.title,
        pageName: AdobeAnalyticsConst.PAGE_IDENTIFIER.SIGN_OUT,
        referrer: document.referrer,
      },
      user: {
        consent: { C0001: true, C0002: true, C0003: true, C0004: true },
        isLoggedIn,
        account: { intEm: true },
        position: userInfo?.adobeAnalyticsRole ?? AdobeAnalyticsUserRole.ANONYMOUS,
      },
    }),
    [locationInfo, i18n.language, userInfo, isLoggedIn],
  );

  const trackEvent = useEventTracker<void>(AdobeAnalyticsConst.EVENTS.SIGN_OUT, buildPayload);

  return {
    trackSignOut: () => {
      const payload = buildPayload();
      trackEvent();
      sendSignOutBeacon({ event: AdobeAnalyticsConst.EVENTS.SIGN_OUT, ...payload });
    },
  };
};

export default useSignOut;
